import { useEffect, useState, Fragment, useCallback } from 'react'
import {
  ScrollView,
  Text,
  TouchableOpacity,
  View,
  StyleSheet,
  SafeAreaView,
  RefreshControl,
  Switch
} from 'react-native'
import { Ionicons, Feather } from '@expo/vector-icons'

// styles
import { colorPalette, fontPalette, globalStyles } from '../../static/styles/global'

// components
import Card from '../../components/shared/card/Card'
import CardSeparator from '../../components/shared/card/CardSeparator'
import HomeDetailsHeader from '../../components/home/HomeDetailsHeader'
import ScreenStatus from '../../components/home/ScreenStatus'
import MessageItem from '../../components/communication/MessageItem'
import MyScrollView from '../../components/shared/navigator/MyScrollView'

// stores
import { useCommunicateStore } from '../../stores/communicate'

// skeleton
import CommunicateSkeleton from '../../skeletons/CommunicateSkeleton'
import CustomButton from '../../components/shared/button/Button'
import HomeMessages from '../../components/home/HomeMessages'
import { messageResponseTitle } from '../../helpers/communicateHelper'
//translation
import { useTranslation } from 'react-i18next'
import { useFocusEffect } from '@react-navigation/native'
import React from 'react'
import { useHomeStore } from '../../stores/home'

const CommunicateWithAdminScreen = ({ navigation, route }): JSX.Element => {
  const { t } = useTranslation()
  const message = t('message')
  const messagess = t('messeges')

  const [isMsgBoxScrolled, setIsMsgBoxScrolled] = useState(false)
  const [refreshing, setRefreshing] = useState(false)

  // params
  const { params } = route
  const { mostRecentUserOnBoardings: userId, refresh } = params
  const homeStore = useHomeStore()

  const handleMsgBoxScroll = () => {
    if (!isMsgBoxScrolled) setIsMsgBoxScrolled(true)
  }

  // stores' data
  const {
    messages: messages,
    loading: loading,
    error: error,
    fetchAllMessages,
    getChattedEmployees,
    chattedEmployees,
    getConversation,
    conversation,
    updateIsEmployeeMessagingEnabled
  } = useCommunicateStore()

  // pull down to refresh the screen
  const onRefresh = useCallback(() => {
    setRefreshing(true)
    setTimeout(() => {
      navigation.replace('CommunicateWithAdmin', { refresh: Math.random() })
      setRefreshing(false)
    }, 1000)
  }, [])

  const handleToggleMessaging = useCallback(async (val: boolean) => {
    if (userId) {
      try {
        await updateIsEmployeeMessagingEnabled(userId, val)
      } catch (err) {
        console.error("Failed to update messaging preference:", err)
      }
    }
  }, [userId, updateIsEmployeeMessagingEnabled])




  useFocusEffect(
    React.useCallback(() => {
      const fetchData = async () => {
        fetchAllMessages(userId)
      };

      fetchData();

    }, [refresh, userId])
  );

  useFocusEffect(
    React.useCallback(() => {
      const fetchEmployees = async () => {
        getChattedEmployees(userId)
      };
      if (messages?.isMessagingAllowed) {
        fetchEmployees()
      }

    }, [refresh, userId, messages?.isMessagingAllowed])
  )

  useFocusEffect(
    React.useCallback(() => {
      const fetchConversation = async () => {
        if (userId) {
          try {
            await getConversation(userId)
          } catch (err) {
            console.error("Failed to refresh conversation:", err)
          }
        }
      }
      fetchConversation()
    }, [userId, getConversation])
  )

  if (loading) return <CommunicateSkeleton />

  if (error) return <ScreenStatus message={error} />


  return (
    messages && (
      <MyScrollView
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        <View style={[globalStyles.primaryBgColor, globalStyles.containerBand]}>
          <View style={[globalStyles.horizontalPadGap]}>
            <HomeDetailsHeader
              title={t('Messages')}
            />
          </View>
          {messages?.isMessagingAllowed && homeStore?.homeData?.isOnboarded &&
            <View style={[globalStyles.hDirection, globalStyles.vCenterItems, { justifyContent: 'flex-end', paddingHorizontal: 16, marginTop: 12, marginBottom: 12 }]}>
              <Text style={[globalStyles.text, globalStyles.fwBold, { color: '#fff', marginRight: 8, fontSize: 14 }]}>
                {conversation?.isEmployeeMessagingEnabled ? t('employeemessagingenabled') : t('employeemessagingdisabled')}
              </Text>
              <Switch
                trackColor={{ false: '#767577', true: colorPalette.secondary }}
                thumbColor={conversation?.isEmployeeMessagingEnabled ? '#fff' : '#f4f3f4'}
                ios_backgroundColor="#3e3e3e"
                onValueChange={handleToggleMessaging}
                value={!!conversation?.isEmployeeMessagingEnabled}
              />
            </View>}
        </View>
        <View style={[globalStyles.containerOverlap, globalStyles.mb4]}>

          <Card>
            <Text style={[
              globalStyles.text,
              globalStyles.fwBold,
              globalStyles.fs18,
              globalStyles.blackTextColor,
              globalStyles.mb2
            ]}>{t('messagehiringmanager')}</Text>

            <View style={[globalStyles.relativePosition]}>
              <View
                style={[
                  globalStyles.hDirection,
                  globalStyles.vCenterItems,
                  globalStyles.hBetweenItems
                ]}
              >
              </View>
              <CardSeparator light={true} narrowBottom={true} />
              <MyScrollView
                style={[
                  globalStyles.mb1,
                  { maxHeight: 300, marginHorizontal: -20 }
                ]}
                onScroll={handleMsgBoxScroll}
                scrollEventThrottle={1000}
                nestedScrollEnabled={true}
              >
                <View style={[{ marginHorizontal: 20 }]}>
                  {(() => {
                    // Filter out auto-generated system messages (PTO approvals, rejections, etc.)
                    // These are already surfaced via Notifications on the Home screen.
                    const AUTO_MSG_SUBJECTS = [
                      'PTO Request Approved',
                      'PTO Request Rejected',
                      'PTO Request Pending Approval',
                      'PTO Request Cancelled',
                      'Approved your PTO Request',
                      'Declined your PTO Request'
                    ]
                    const filteredMsgs = messages?.messages?.filter(
                      (msg) =>
                        !(
                          msg?.initiatorType === 'A' &&
                          AUTO_MSG_SUBJECTS.includes(msg?.subject.trim())
                        )
                    ) || []
                    return filteredMsgs.length > 0 ? (
                      filteredMsgs.map<JSX.Element>((msgData, idx) => {
                        return (
                          <Fragment key={`message-item-${idx}`}>
                            <MessageItem
                              title={msgData?.subject}
                              from={
                                msgData?.initiatorType === 'A' ? t('admin') : t('you')
                              }
                              sentMessageLabel={
                                messageResponseTitle(msgData?.initiatorType)
                                  .message
                              }
                              sentMessageDate={msgData?.messageSentDate}
                              replyMessageLabel={
                                messageResponseTitle(msgData?.initiatorType)
                                  .response
                              }
                              replyMessageDate={msgData?.messageResponseDate}
                              messageType={msgData.messageType}
                              initiatorType={msgData?.initiatorType}
                              message={msgData?.message}
                              response={msgData?.response}
                              action={() => {
                                navigation.navigate('Message', {
                                  message: msgData
                                })
                              }}
                              unread={msgData.isNew}
                            />
                            {idx < messages.length - 1 && (
                              <CardSeparator
                                light={true}
                                narrowTop={true}
                                narrowBottom={true}
                                withGap={true}
                              />
                            )}
                          </Fragment>
                        )
                      })
                    ) : (
                      <Text
                        style={[
                          globalStyles.text,
                          globalStyles.textCenter,
                          globalStyles.mt1,
                          globalStyles.mb1,
                          globalStyles.blackTextColor
                        ]}
                      >
                        {t('nomessages')}
                      </Text>
                    )
                  }
                  )()}
                </View>
              </MyScrollView>
              {!isMsgBoxScrolled && messages.length > 2 && (
                <View style={{ marginHorizontal: -20 }}>
                  <View style={[styles.showMore]}></View>
                </View>
              )}
            </View>
          </Card>
          <View style={[globalStyles.mt2, globalStyles.mb4]}>
            <CustomButton
              text={t('createnewmessage')}
              onPress={() => {
                navigation.navigate('NewMessage', { userId })
              }}
            />
          </View>
        </View>

        {messages?.isMessagingAllowed && conversation?.isEmployeeMessagingEnabled && homeStore?.homeData?.isOnboarded &&

          < View style={[globalStyles.containerOverlap, globalStyles.mb4]}>
            <Card>
              <View style={[globalStyles.relativePosition]}>
                <Text
                  style={[
                    globalStyles.text,
                    globalStyles.fwBold,
                    globalStyles.fs18,
                    globalStyles.blackTextColor,
                    globalStyles.mb2
                  ]}
                >
                  {t('employeemessages')}
                </Text>
                <CardSeparator light={true} narrowBottom={true} />
                <MyScrollView
                  style={[
                    globalStyles.mb1,
                    { maxHeight: 300, marginHorizontal: -20 }
                  ]}
                  onScroll={handleMsgBoxScroll}
                  scrollEventThrottle={1000}
                  nestedScrollEnabled={true}
                >
                  <View style={[{ marginHorizontal: 20 }]}>
                    {chattedEmployees?.employees?.length > 0 ? (
                      chattedEmployees?.employees?.map<JSX.Element>((employee, idx) => {
                        return (
                          <Fragment key={idx}>
                            <TouchableOpacity style={styles.container} onPress={() => navigation.navigate("CommunicateWithEmployee", { pUserKey: employee.essUserKey, pObKey: employee.essUserOnBoardingKey })}>
                              <View style={[globalStyles.hDirection, globalStyles.hBetweenItems]}>
                                <Text style={{ fontFamily: fontPalette.primaryBold }}>{employee?.name}</Text>
                                {employee?.unreadCount > 0 ?
                                  <View
                                    style={{
                                      width: 24,
                                      height: 24,
                                      borderRadius: 12,
                                      backgroundColor: '#1976D2',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <Text
                                      style={{
                                        color: '#fff',
                                        fontSize: 12,
                                        fontFamily: fontPalette.primaryBold,
                                      }}
                                    >
                                      {employee?.unreadCount > 99 ? '99+' : employee?.unreadCount}
                                    </Text>
                                  </View> : <></>}
                              </View>
                              <View>
                                <Text>{employee?.lastMessage}</Text>
                              </View>

                            </TouchableOpacity>
                            {idx < chattedEmployees?.employees?.length - 1 && (
                              <CardSeparator
                                light={true}
                                narrowTop={true}
                                narrowBottom={true}
                                withGap={true}
                              />
                            )}
                          </Fragment>

                        )
                      })
                    ) : (
                      <Text
                        style={[
                          globalStyles.text,
                          globalStyles.textCenter,
                          globalStyles.mt1,
                          globalStyles.mb1,
                          globalStyles.blackTextColor
                        ]}
                      >
                        {t('nomessages')}
                      </Text>
                    )}
                  </View>
                </MyScrollView>

              </View>
            </Card>
            <View style={[globalStyles.mt2, globalStyles.mb4]}>
              <CustomButton
                text={t('createnewmessage')}
                onPress={() => {
                  navigation.navigate('NewMessage', { userId, isEmployeeMessage: true })
                }}
              />
            </View>
          </View >}
      </MyScrollView >
    )
  )
}
const styles = StyleSheet.create({
  showMore: {
    position: 'absolute',
    bottom: -20,
    width: '100%',
    height: 70,
    backgroundColor: 'rgba(255, 255, 255, .7)',
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10
  },
  container: {
    flex: 1,
    paddingHorizontal: 16,
    paddingVertical: 16,
    backgroundColor: colorPalette.lighter,
    borderRadius: 8
  }
})

export default CommunicateWithAdminScreen
